import React, { useState, useEffect } from "react";
import { useParams } from 'react-router-dom';
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { deleteRatePrice, getRatePriceByResortId } from "../../../../../../../apis/property";
import EditRatePrice from "./editRatePrice";
import CreateRatePrice from "./createRatePrice";
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CashIcon,
  CalendarIcon,
  UserGroupIcon,
  SearchIcon,
  ViewGridIcon,
  ViewListIcon,
  FilterIcon,
  CurrencyDollarIcon,
} from "@heroicons/react/outline";

const RatePrice = () => {
  const { id } = useParams();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [rate, setRate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedRateId, setSelectedRateId] = useState(null);
  const [ratePrices, setRatePrices] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState("list");

  const fetchRatePrice = async () => {
    try {
      const response = await getRatePriceByResortId(id);
      const ratePrice = response.data.rates;
      
      const rateMap = ratePrice.reduce((acc, room) => {
        const rate = room.name || room.code || `Rate mapping ${room.room_rate_id}`;
        const id = room.id;
        acc[id] = rate;
        return acc;
      }, {});

      setRate(rateMap);
      setRatePrices(ratePrice);
      setLoading(false);
    } catch (error) {
      console.log('Error fetching rate prices');
      setLoading(false);
    }
  };

  // Fetch the initial list when the property page mounts.
  useEffect(() => {
    fetchRatePrice();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = async () => {
    setIsModalOpen(false);
    await fetchRatePrice();
  };

  const openEditModal = (rateId) => {
    setSelectedRateId(rateId);
    setIsEditModalOpen(true);
  };

  const closeEditModal = async () => {
    setIsEditModalOpen(false);
    await fetchRatePrice();
    setSelectedRateId(null);
  };

  const handleDelete = async (rate_price_id) => {
    if (window.confirm("Are you sure you want to delete this rate price?")) {
      try {
        await deleteRatePrice(rate_price_id);
        toast.success("Rate price deleted successfully");
        await fetchRatePrice();
      } catch (error) {
        toast.error("Failed to delete rate price");
      }
    }
  };

  // Filter rate prices based on search
  const filteredRatePrices = ratePrices?.filter((item) =>
    item.base_price?.toString().includes(searchQuery) ||
    item.single_occupancy?.toString().includes(searchQuery) ||
    item.double_occupancy?.toString().includes(searchQuery) ||
    rate[item.id]?.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];

  const priceColors = [
    'from-teal-400 to-teal-600',
    'from-cyan-400 to-cyan-600',
    'from-sky-400 to-sky-600',
    'from-blue-400 to-blue-600',
    'from-indigo-400 to-indigo-600',
  ];

  return (
    <div className="rate-price-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Rate Pricing Configuration</h1>
          <p className="page-subtitle">Manage pricing for different occupancy types</p>
        </div>
        <button onClick={openModal} className="create-btn">
          <PlusIcon className="w-5 h-5" />
          <span>Add Rate Price</span>
        </button>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card stat-card-teal">
          <div className="stat-icon">
            <CashIcon className="w-6 h-6" />
          </div>
          <div>
            <p className="stat-label">Total Rates</p>
            <h3 className="stat-value">{ratePrices?.length || 0}</h3>
          </div>
        </div>
        <div className="stat-card stat-card-cyan">
          <div className="stat-icon">
            <CurrencyDollarIcon className="w-6 h-6" />
          </div>
          <div>
            <p className="stat-label">Active Pricing</p>
            <h3 className="stat-value">{ratePrices?.length || 0}</h3>
          </div>
        </div>
      </div>

      {/* Filters and View Toggle */}
      <div className="filters-section">
        <div className="search-wrapper">
          <SearchIcon className="search-icon" />
          <input
            type="text"
            placeholder="Search rate prices..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filter-actions">
          <div className="results-count">
            <span>{filteredRatePrices.length} {filteredRatePrices.length === 1 ? 'Price' : 'Prices'}</span>
          </div>

          <button className="filter-btn">
            <FilterIcon className="w-4 h-4" />
            <span>Filter</span>
          </button>

          <div className="view-toggle">
            <button
              onClick={() => setViewMode("list")}
              className={`toggle-btn ${viewMode === "list" ? "active" : ""}`}
            >
              <ViewListIcon className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode("grid")}
              className={`toggle-btn ${viewMode === "grid" ? "active" : ""}`}
            >
              <ViewGridIcon className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Content Display */}
      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading rate prices...</p>
        </div>
      ) : viewMode === "list" ? (
        /* List/Table View */
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>
                  <input type="checkbox" className="checkbox" />
                </th>
                <th>Base Price</th>
                <th>Single Occ.</th>
                <th>Double Occ.</th>
                <th>Extra Adult</th>
                <th>Start Date</th>
                <th>End Date</th>
                <th>Room Rate</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRatePrices.map((rateP) => (
                <tr key={rateP.id}>
                  <td>
                    <input type="checkbox" className="checkbox" />
                  </td>
                  <td>
                    <div className="price-cell">
                      <CashIcon className="w-4 h-4 text-teal-500" />
                      <span className="price-value">${rateP.base_price}</span>
                    </div>
                  </td>
                  <td>
                    <span className="occupancy-badge">${rateP.single_occupancy}</span>
                  </td>
                  <td>
                    <span className="occupancy-badge">${rateP.double_occupancy}</span>
                  </td>
                  <td>
                    <span className="extra-badge">${rateP.extra_adult}</span>
                  </td>
                  <td>
                    <div className="date-cell">
                      <CalendarIcon className="w-4 h-4 text-gray-400" />
                      <span>{rateP.start_date}</span>
                    </div>
                  </td>
                  <td>
                    <div className="date-cell">
                      <CalendarIcon className="w-4 h-4 text-gray-400" />
                      <span>{rateP.end_date}</span>
                    </div>
                  </td>
                  <td>
                    <span className="rate-badge">{rate[rateP.id] || 'N/A'}</span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        onClick={() => openEditModal(rateP.id)}
                        className="action-btn action-btn-edit"
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(rateP.id)}
                        className="action-btn action-btn-delete"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredRatePrices.length === 0 && (
            <div className="empty-state">
              <CashIcon className="w-16 h-16 text-gray-400" />
              <h3>No Rate Prices Found</h3>
              <p>
                {searchQuery
                  ? "Try adjusting your search criteria"
                  : "Get started by creating your first rate price"}
              </p>
            </div>
          )}
        </div>
      ) : (
        /* Grid View */
        <div className="grid-view">
          {filteredRatePrices.map((rateP, idx) => (
            <div key={rateP.id} className="price-card">
              <div className={`price-card-header bg-gradient-to-br ${priceColors[idx % priceColors.length]}`}>
                <div className="price-card-icon">
                  <CashIcon className="w-6 w-6" />
                </div>
                <h3 className="price-card-title">${rateP.base_price}</h3>
                <p className="price-card-subtitle">Base Price</p>
              </div>
              <div className="price-card-body">
                <div className="price-info-row">
                  <div className="price-info-item">
                    <UserGroupIcon className="w-4 h-4 text-gray-400" />
                    <div>
                      <p className="info-label">Single</p>
                      <p className="info-value">${rateP.single_occupancy}</p>
                    </div>
                  </div>
                  <div className="price-info-item">
                    <UserGroupIcon className="w-4 h-4 text-gray-400" />
                    <div>
                      <p className="info-label">Double</p>
                      <p className="info-value">${rateP.double_occupancy}</p>
                    </div>
                  </div>
                </div>
                <div className="price-info-item">
                  <UserGroupIcon className="w-4 h-4 text-gray-400" />
                  <div>
                    <p className="info-label">Extra Adult</p>
                    <p className="info-value">${rateP.extra_adult}</p>
                  </div>
                </div>
                <div className="price-date-range">
                  <CalendarIcon className="w-4 h-4 text-teal-500" />
                  <span>{rateP.start_date} - {rateP.end_date}</span>
                </div>
                <div className="price-rate-name">
                  <span className="rate-badge">{rate[rateP.id] || 'N/A'}</span>
                </div>
              </div>
              <div className="price-card-footer">
                <button
                  onClick={() => openEditModal(rateP.id)}
                  className="price-action-btn price-edit-btn"
                >
                  <PencilIcon className="w-4 h-4" />
                  <span>Edit</span>
                </button>
                <button
                  onClick={() => handleDelete(rateP.id)}
                  className="price-action-btn price-delete-btn"
                >
                  <TrashIcon className="w-4 h-4" />
                  <span>Delete</span>
                </button>
              </div>
            </div>
          ))}

          {filteredRatePrices.length === 0 && (
            <div className="empty-state grid-empty">
              <CashIcon className="w-16 h-16 text-gray-400" />
              <h3>No Rate Prices Found</h3>
              <p>
                {searchQuery
                  ? "Try adjusting your search criteria"
                  : "Get started by creating your first rate price"}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Modals */}
      <CreateRatePrice isOpen={isModalOpen} onClose={closeModal} />
      <EditRatePrice
        isOpen={isEditModalOpen}
        onClose={closeEditModal}
        selectedRateId={selectedRateId}
      />

      {/* Styles */}
      <style jsx>{`
        .rate-price-page {
          padding: 0;
        }

        .page-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 2rem;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .page-title {
          font-size: 2rem;
          font-weight: 800;
          color: #1f2937;
          margin: 0 0 0.5rem 0;
        }

        .page-subtitle {
          font-size: 1rem;
          color: #6b7280;
          margin: 0;
        }

        .create-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1.5rem;
          background: linear-gradient(135deg, #14b8a6, #0d9488);
          color: white;
          font-weight: 600;
          border-radius: 0.75rem;
          border: none;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 4px 6px -1px rgba(20, 184, 166, 0.3);
        }

        .create-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 15px -3px rgba(20, 184, 166, 0.4);
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1.5rem;
          margin-bottom: 2rem;
        }

        .stat-card {
          background: white;
          padding: 1.5rem;
          border-radius: 1rem;
          border: 1px solid #e5e7eb;
          display: flex;
          align-items: center;
          gap: 1rem;
          transition: all 0.3s ease;
        }

        .stat-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
        }

        .stat-icon {
          width: 3rem;
          height: 3rem;
          border-radius: 0.75rem;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
        }

        .stat-card-teal .stat-icon {
          background: linear-gradient(135deg, #14b8a6, #0d9488);
        }

        .stat-card-cyan .stat-icon {
          background: linear-gradient(135deg, #06b6d4, #0891b2);
        }

        .stat-label {
          font-size: 0.875rem;
          color: #6b7280;
          margin: 0 0 0.25rem 0;
        }

        .stat-value {
          font-size: 1.5rem;
          font-weight: 700;
          color: #1f2937;
          margin: 0;
        }

        .filters-section {
          background: white;
          padding: 1.5rem;
          border-radius: 1rem;
          border: 1px solid #e5e7eb;
          margin-bottom: 1.5rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .search-wrapper {
          position: relative;
          flex: 1;
          max-width: 400px;
        }

        .search-icon {
          position: absolute;
          left: 1rem;
          top: 50%;
          transform: translateY(-50%);
          width: 1.25rem;
          height: 1.25rem;
          color: #9ca3af;
        }

        .search-input {
          width: 100%;
          padding: 0.75rem 1rem 0.75rem 3rem;
          border: 2px solid #e5e7eb;
          border-radius: 0.75rem;
          font-size: 0.875rem;
          transition: all 0.2s ease;
        }

        .search-input:focus {
          outline: none;
          border-color: #14b8a6;
          box-shadow: 0 0 0 3px rgba(20, 184, 166, 0.1);
        }

        .filter-actions {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .results-count {
          padding: 0.5rem 1rem;
          background: #f3f4f6;
          border-radius: 0.5rem;
          font-size: 0.875rem;
          font-weight: 600;
          color: #374151;
        }

        .filter-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1rem;
          background: white;
          border: 2px solid #e5e7eb;
          border-radius: 0.75rem;
          font-size: 0.875rem;
          font-weight: 500;
          color: #374151;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .filter-btn:hover {
          border-color: #14b8a6;
          color: #14b8a6;
        }

        .view-toggle {
          display: flex;
          background: #f3f4f6;
          border-radius: 0.5rem;
          padding: 0.25rem;
        }

        .toggle-btn {
          padding: 0.5rem;
          background: transparent;
          border: none;
          border-radius: 0.375rem;
          color: #6b7280;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .toggle-btn.active {
          background: white;
          color: #14b8a6;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
        }

        .loading-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 4rem 2rem;
          background: white;
          border-radius: 1rem;
          border: 1px solid #e5e7eb;
        }

        .spinner {
          width: 3rem;
          height: 3rem;
          border: 4px solid #e5e7eb;
          border-top-color: #14b8a6;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }

        .loading-state p {
          margin-top: 1rem;
          color: #6b7280;
          font-size: 0.875rem;
        }

        .table-container {
          background: white;
          border-radius: 1rem;
          border: 1px solid #e5e7eb;
          overflow: hidden;
        }

        .data-table {
          width: 100%;
          border-collapse: collapse;
        }

        .data-table thead {
          background: #f9fafb;
          border-bottom: 1px solid #e5e7eb;
        }

        .data-table th {
          padding: 1rem 1.5rem;
          text-align: left;
          font-size: 0.75rem;
          font-weight: 600;
          color: #6b7280;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .data-table td {
          padding: 1rem 1.5rem;
          border-bottom: 1px solid #f3f4f6;
        }

        .data-table tbody tr {
          transition: background 0.2s;
        }

        .data-table tbody tr:hover {
          background: #f9fafb;
        }

        .checkbox {
          width: 1rem;
          height: 1rem;
          border-radius: 0.25rem;
          cursor: pointer;
        }

        .price-cell {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .price-value {
          font-weight: 700;
          color: #0d9488;
          font-size: 1rem;
        }

        .occupancy-badge {
          display: inline-block;
          padding: 0.25rem 0.75rem;
          background: #dbeafe;
          color: #1e40af;
          border-radius: 9999px;
          font-size: 0.75rem;
          font-weight: 600;
        }

        .extra-badge {
          display: inline-block;
          padding: 0.25rem 0.75rem;
          background: #fef3c7;
          color: #92400e;
          border-radius: 9999px;
          font-size: 0.75rem;
          font-weight: 600;
        }

        .date-cell {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.875rem;
          color: #6b7280;
        }

        .rate-badge {
          display: inline-block;
          padding: 0.25rem 0.75rem;
          background: #f0fdfa;
          color: #0f766e;
          border-radius: 9999px;
          font-size: 0.75rem;
          font-weight: 600;
        }

        .action-buttons {
          display: flex;
          gap: 0.5rem;
        }

        .action-btn {
          padding: 0.5rem;
          border: none;
          border-radius: 0.5rem;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .action-btn-edit {
          background: #eff6ff;
          color: #2563eb;
        }

        .action-btn-edit:hover {
          background: #dbeafe;
          transform: scale(1.1);
        }

        .action-btn-delete {
          background: #fef2f2;
          color: #dc2626;
        }

        .action-btn-delete:hover {
          background: #fee2e2;
          transform: scale(1.1);
        }

        .grid-view {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
          gap: 1.5rem;
        }

        .price-card {
          background: white;
          border-radius: 1rem;
          border: 1px solid #e5e7eb;
          overflow: hidden;
          transition: all 0.3s ease;
        }

        .price-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
        }

        .price-card-header {
          padding: 1.5rem;
          color: white;
          text-align: center;
          position: relative;
          overflow: hidden;
        }

        .price-card-header::before {
          content: '';
          position: absolute;
          top: 0;
          right: 0;
          width: 200px;
          height: 200px;
          background: radial-gradient(circle, rgba(255, 255, 255, 0.2) 0%, transparent 70%);
          border-radius: 50%;
          transform: translate(40%, -40%);
        }

        .price-card-icon {
          width: 3rem;
          height: 3rem;
          background: rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(10px);
          border-radius: 0.75rem;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1rem;
        }

        .price-card-title {
          font-size: 2rem;
          font-weight: 800;
          margin: 0;
        }

        .price-card-subtitle {
          font-size: 0.875rem;
          opacity: 0.9;
          margin: 0.5rem 0 0 0;
        }

        .price-card-body {
          padding: 1.5rem;
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .price-info-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
        }

        .price-info-item {
          display: flex;
          align-items: flex-start;
          gap: 0.75rem;
        }

        .info-label {
          font-size: 0.75rem;
          color: #9ca3af;
          margin: 0 0 0.25rem 0;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .info-value {
          font-size: 0.875rem;
          color: #1f2937;
          font-weight: 700;
          margin: 0;
        }

        .price-date-range {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem;
          background: #f9fafb;
          border-radius: 0.5rem;
          font-size: 0.875rem;
          color: #6b7280;
        }

        .price-rate-name {
          display: flex;
          justify-content: center;
        }

        .price-card-footer {
          padding: 1rem 1.5rem;
          background: #f9fafb;
          border-top: 1px solid #e5e7eb;
          display: flex;
          gap: 0.75rem;
        }

        .price-action-btn {
          flex: 1;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          padding: 0.625rem 1rem;
          font-size: 0.875rem;
          font-weight: 600;
          border-radius: 0.5rem;
          border: none;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .price-edit-btn {
          background: #eff6ff;
          color: #2563eb;
        }

        .price-edit-btn:hover {
          background: #dbeafe;
          transform: translateY(-1px);
        }

        .price-delete-btn {
          background: #fef2f2;
          color: #dc2626;
        }

        .price-delete-btn:hover {
          background: #fee2e2;
          transform: translateY(-1px);
        }

        .empty-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 4rem 2rem;
          text-align: center;
        }

        .empty-state h3 {
          font-size: 1.25rem;
          font-weight: 700;
          color: #1f2937;
          margin: 1rem 0 0.5rem 0;
        }

        .empty-state p {
          color: #6b7280;
          margin: 0;
        }

        .grid-empty {
          grid-column: 1 / -1;
        }

        @media (max-width: 1024px) {
          .page-header {
            flex-direction: column;
            align-items: stretch;
          }

          .create-btn {
            width: 100%;
            justify-content: center;
          }

          .filters-section {
            flex-direction: column;
            align-items: stretch;
          }

          .search-wrapper {
            max-width: 100%;
          }

          .filter-actions {
            justify-content: space-between;
          }
        }

        @media (max-width: 768px) {
          .data-table {
            display: block;
            overflow-x: auto;
          }

          .grid-view {
            grid-template-columns: 1fr;
          }

          .price-info-row {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default RatePrice;
